/**
 * Editor Header — sticky top bar with save/publish, dirty indicator, markdown preview toggle.
 * Ported from PaaS EditorHeader.tsx (156 lines)
 */
import {
  ArrowLeft, Clock, Eye, Save, Send, MoreHorizontal, CheckCircle2,
  Download, Trash2, UploadCloud, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { ArticleState } from '@argusai/shared';

interface EditorHeaderProps {
  title: string;
  status: ArticleState;
  isDirty: boolean;
  isSaving?: boolean;
  updatedAt: string;
  viewMode: 'edit' | 'markdown';
  onBack?: () => void;
  onSave: (status?: 'draft' | 'published') => void;
  onDelete: () => void;
  onToggleViewMode: () => void;
  onStatusChange: (status: 'draft' | 'published') => void;
  onExport: (format: 'md' | 'pdf') => void;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const now = Date.now();
    const diff = now - d.getTime();
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString();
  } catch { return 'recently'; }
}

export function EditorHeader({
  title, status, isDirty, isSaving = false, updatedAt, viewMode,
  onBack, onSave, onDelete, onToggleViewMode, onStatusChange, onExport,
}: EditorHeaderProps) {
  return (
    <div className="h-14 border-b flex items-center justify-between px-4 bg-background sticky top-0 z-20">
      {/* Left side */}
      <div className="flex items-center gap-3">
        {onBack && (
          <Button type="button" variant="ghost" size="icon" onClick={onBack} className="h-8 w-8 text-muted-foreground hover:text-foreground" aria-label="Back" title="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        <div className="h-4 w-px bg-border" />
        <span className="font-medium text-sm truncate max-w-[300px]">
          {title || 'Untitled Document'}
        </span>
      </div>

      {/* Right side */}
      <div className="flex items-center gap-2">
        {/* Dirty indicator */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground mr-2">
          {isDirty ? (
            <span className="text-amber-600 flex items-center gap-1.5 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              Unsaved changes
            </span>
          ) : (
            <span className="flex items-center gap-1 opacity-60">
              <Clock className="h-3 w-3" />
              Edited {formatDate(updatedAt)}
            </span>
          )}
        </div>

        {/* Preview MD toggle */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onToggleViewMode}
          className={cn('text-xs h-8 px-3 gap-2', viewMode === 'markdown' && 'bg-muted text-primary font-medium')}
        >
          <Eye className="h-3.5 w-3.5" />
          {viewMode === 'markdown' ? 'Back to Editor' : 'Preview MD'}
        </Button>

        <div className="h-4 w-px bg-border mx-1" />

        {/* Save Draft */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onSave('draft')}
          disabled={!isDirty || isSaving}
          className="text-xs h-8"
          title={isSaving ? 'Saving...' : !isDirty ? 'No unsaved changes' : 'Save as draft'}
        >
          {isSaving
            ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            : <Save className="mr-2 h-3.5 w-3.5" />}
          Save Draft
        </Button>

        {/* Publish */}
        <Button
          type="button"
          size="sm"
          onClick={() => onSave('published')}
          disabled={isSaving}
          className="text-xs h-8"
          title={isSaving ? 'Saving...' : status === 'published' ? 'Re-publish with current edits' : 'Publish article'}
        >
          {isSaving
            ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            : <Send className="mr-2 h-3.5 w-3.5" />}
          {isSaving ? 'Saving...' : 'Publish'}
        </Button>

        {/* More menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" size="icon" variant="ghost" className="h-8 w-8" aria-label="Document actions" title="More actions">
              <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Document Status</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => onStatusChange(status === 'published' ? 'draft' : 'published')}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              {status === 'published' ? 'Move to Draft' : 'Mark as Published'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Export</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => onExport('md')}>
              <Download className="mr-2 h-4 w-4 text-muted-foreground" /> Export as Markdown
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onDelete} className="text-red-600 focus:text-red-600 focus:bg-red-50">
              <Trash2 className="mr-2 h-4 w-4" /> Delete Permanently
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
